import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../BaseComponents/Modal';
import DatePickerComp from '../BaseComponents/DatePicker';
import Select, { SelectOption } from '../BaseComponents/Select';
import TextArea from '../BaseComponents/TextArea';
import TextInput from '../BaseComponents/TextInput';
import Button from '../BaseComponents/Button';
import './CreatePmpAlertModal.scss';
import { createPmpAlert, PmpAlertItem, updatePmpAlert } from '../../api/pmp-alert';
import { getActivePmpModalities, PmpModality } from '../../api/pmpModalities';
import {
  getMemberPersonalisedMonitoringPlanHistory,
  MemberPersonalisedMonitoringPlanHistoryItem,
} from '../../api/members';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (saved: PmpAlertItem) => void;
  memberId?: string;
  initialAlert?: PmpAlertItem | null;
};

const LEADING_ARTICLE = /^(a|an|the)\b/i;

const suggestModalityDisplay = (name: string): string => {
  const trimmed = name.trim();
  if (/^full[\s-]*body[\s-]*scan$/i.test(trimmed)) {
    return 'full body scan';
  }
  const acronyms = new Set(['MRI', 'CT', 'DEXA']);
  return trimmed
    .split(/\s+/)
    .map((word) => {
      if (/^x-ray$/i.test(word)) return 'X-ray';
      const letters = word.replace(/[^A-Za-z]/g, '');
      if (acronyms.has(letters.toUpperCase())) return letters.toUpperCase();
      return word.toLowerCase();
    })
    .join(' ');
};

const formatPrice = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '';
  }
  return Number(value).toFixed(2);
};

const CreatePmpAlertModal = ({
  isOpen,
  onClose,
  onSubmit,
  memberId,
  initialAlert,
}: Props) => {
  const [planOptions, setPlanOptions] = useState<SelectOption[]>([]);
  const [modalityOptions, setModalityOptions] = useState<SelectOption[]>([]);
  const [modalities, setModalities] = useState<PmpModality[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isMonthOnly, setIsMonthOnly] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<SelectOption | null>(null);
  const [selectedModality, setSelectedModality] = useState<SelectOption | null>(null);
  const [findingTitle, setFindingTitle] = useState('');
  const [findingContext, setFindingContext] = useState('');
  const [clinicalContext, setClinicalContext] = useState('');
  const [modalityDisplay, setModalityDisplay] = useState('');
  const [modalityPrep, setModalityPrep] = useState('');
  const [fullPrice, setFullPrice] = useState('');
  const [memberPrice, setMemberPrice] = useState('');
  const displayEditedRef = useRef(false);
  const pricesEditedRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const initialAlertRef = useRef(initialAlert);
  initialAlertRef.current = initialAlert;
  const isEdit = Boolean(initialAlert?.id);

  const selectedModalityData = useMemo(
    () =>
      modalities.find(
        (modality) => Number(modality.id) === Number(selectedModality?.key)
      ) || null,
    [modalities, selectedModality]
  );

  const parsePrice = (value: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return parsed;
  };

  const fullPriceError =
    fullPrice.trim() && parsePrice(fullPrice) === null ? 'Enter a valid full price' : '';
  const memberPriceError =
    memberPrice.trim() && parsePrice(memberPrice) === null
      ? 'Enter a valid member price'
      : '';

  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(null);
      setIsMonthOnly(false);
      setSelectedPlan(null);
      setSelectedModality(null);
      setFindingTitle('');
      setFindingContext('');
      setClinicalContext('');
      setModalityDisplay('');
      setModalityPrep('');
      setFullPrice('');
      setMemberPrice('');
      displayEditedRef.current = false;
      pricesEditedRef.current = false;
      setIsSubmitting(false);
      setIsTouched(false);
      return;
    }

    let cancelled = false;

    const applyAlert = (
      alert: PmpAlertItem,
      plans: SelectOption[],
      modalityChoices: SelectOption[]
    ) => {
      if (alert.scheduled_month_year) {
        const [year, month] = alert.scheduled_month_year.split('-').map(Number);
        if (year && month) {
          setIsMonthOnly(true);
          setSelectedDate(new Date(year, month - 1, 1));
        }
      } else if (alert.scheduled_date) {
        setIsMonthOnly(false);
        setSelectedDate(new Date(alert.scheduled_date));
      } else {
        setIsMonthOnly(false);
        setSelectedDate(null);
      }

      const planOption =
        plans.find((option) => Number(option.key) === Number(alert.pmp_plan_id)) ||
        (alert.pmp_plan_id
          ? {
              key: alert.pmp_plan_id,
              value: alert.pmp_reference_number || `PMP plan #${alert.pmp_plan_id}`,
            }
          : null);
      setSelectedPlan(planOption);

      const modalityId = alert.modality?.id ?? alert.modality_id;
      const modalityOption =
        modalityChoices.find((option) => Number(option.key) === Number(modalityId)) ||
        (modalityId
          ? {
              key: modalityId,
              value: alert.modality?.name || 'Modality',
            }
          : null);
      setSelectedModality(modalityOption);
      pricesEditedRef.current = true;
      setFullPrice(formatPrice(alert.full_price));
      setMemberPrice(formatPrice(alert.member_price));
      setFindingTitle(alert.finding_title || '');
      setFindingContext(alert.finding_context || '');
      setClinicalContext(alert.clinical_context || '');
      setModalityPrep(alert.modality_prep || '');
      if (alert.modality_display) {
        displayEditedRef.current = true;
        setModalityDisplay(alert.modality_display);
      } else {
        displayEditedRef.current = false;
        setModalityDisplay('');
      }
    };

    const loadOptions = async () => {
      if (!memberId) {
        toast.error('Member is required to save a PMP alert');
        return;
      }

      setIsLoadingOptions(true);
      try {
        const [history, activeModalities] = await Promise.all([
          getMemberPersonalisedMonitoringPlanHistory(memberId),
          getActivePmpModalities(),
        ]);
        if (cancelled) return;

        const plansWithRefs = (history || []).filter(
          (item: MemberPersonalisedMonitoringPlanHistoryItem) =>
            Boolean(item.reference_number?.trim())
        );
        const plans = plansWithRefs.map((item) => ({
          key: item.id,
          value: item.reference_number || `PMP plan #${item.id}`,
        }));
        const modalityChoices = activeModalities.map((modality) => ({
          key: modality.id,
          value: modality.name,
        }));

        setPlanOptions(plans);
        setModalities(activeModalities);
        setModalityOptions(modalityChoices);

        const alert = initialAlertRef.current;
        if (alert) {
          applyAlert(alert, plans, modalityChoices);
        }
      } catch (error) {
        console.error('Failed to load PMP alert options', error);
        toast.error('Unable to load PMP plans or modalities. Please try again.');
      } finally {
        if (!cancelled) setIsLoadingOptions(false);
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen, memberId]);

  useEffect(() => {
    if (!selectedModalityData || displayEditedRef.current) return;
    setModalityDisplay(suggestModalityDisplay(selectedModalityData.name));
  }, [selectedModalityData]);

  useEffect(() => {
    if (!selectedModalityData || pricesEditedRef.current) return;
    setFullPrice(formatPrice(selectedModalityData.full_price));
    setMemberPrice(formatPrice(selectedModalityData.member_price));
  }, [selectedModalityData]);

  const modalityDisplayError = useMemo(() => {
    const value = modalityDisplay.trim();
    if (!value) return 'Modality display is required';
    if (LEADING_ARTICLE.test(value)) {
      return 'Use a noun phrase with no article, such as "ultrasound of the liver"';
    }
    return '';
  }, [modalityDisplay]);

  const isValid = useMemo(() => {
    if (!selectedDate || !selectedPlan || !selectedModality) return false;
    if (!findingTitle.trim() || !findingContext.trim()) return false;
    if (modalityDisplayError) return false;
    if (fullPriceError || memberPriceError) return false;
    return true;
  }, [
    selectedDate,
    selectedPlan,
    selectedModality,
    findingTitle,
    findingContext,
    modalityDisplayError,
    fullPriceError,
    memberPriceError,
  ]);

  const isSubmitDisabled = !isValid || isSubmitting || isLoadingOptions;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsTouched(true);

    if (!selectedDate || !selectedPlan || !selectedModality) return;
    if (!findingTitle.trim() || !findingContext.trim()) return;
    if (modalityDisplayError) return;
    if (fullPriceError || memberPriceError) return;
    if (!memberId) {
      toast.error('Member is required to save a PMP alert');
      return;
    }

    setIsSubmitting(true);
    try {
      let scheduledDate: string | null = null;
      let scheduledMonthYear: string | null = null;

      if (isMonthOnly) {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        scheduledMonthYear = `${year}-${month}`;
      } else {
        const utcDate = new Date(
          selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000
        );
        scheduledDate = utcDate.toISOString();
      }

      const payload = {
        pmp_plan_id: Number(selectedPlan.key),
        finding_title: findingTitle.trim(),
        finding_context: findingContext.trim(),
        clinical_context: clinicalContext.trim() || null,
        modality_display: modalityDisplay.trim(),
        modality_prep: modalityPrep.trim(),
        modality_id: Number(selectedModality.key),
        full_price: parsePrice(fullPrice),
        member_price: parsePrice(memberPrice),
        scheduled_date: scheduledDate,
        scheduled_month_year: scheduledMonthYear,
      };

      const result = initialAlert?.id
        ? await updatePmpAlert({ id: initialAlert.id, ...payload })
        : await createPmpAlert({ member_id: memberId, ...payload });

      onSubmit?.(result);
      toast.success(
        initialAlert?.id
          ? 'PMP alert updated. Reminders follow the due date.'
          : 'PMP alert saved. Email and WhatsApp reminders will go out 30 days and 7 days before the due date.'
      );
      onClose();
    } catch (error) {
      toast.error('Unable to save PMP alert. Please try again.');
      console.error('Failed to save PMP alert', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit PMP Alert' : 'Create PMP Alert'}
      width='560px'
    >
      <form className='pmp-alert-form' onSubmit={handleSubmit}>
        <div className='form-row'>
          <div className='schedule-mode-radios' role='radiogroup' aria-label='Schedule type'>
            <label className='schedule-mode-option'>
              <input
                type='radio'
                name='pmp_alert_schedule_mode'
                checked={!isMonthOnly}
                onChange={() => {
                  if (isMonthOnly) {
                    setIsMonthOnly(false);
                    setSelectedDate(null);
                  }
                }}
              />
              <span>Select date</span>
            </label>
            <label className='schedule-mode-option'>
              <input
                type='radio'
                name='pmp_alert_schedule_mode'
                checked={isMonthOnly}
                onChange={() => {
                  if (!isMonthOnly) {
                    setIsMonthOnly(true);
                    setSelectedDate(null);
                  }
                }}
              />
              <span>Select month</span>
            </label>
          </div>
          </div>
          <div className='form-row'>
          <DatePickerComp
            title={isMonthOnly ? 'Month due *' : 'Date due *'}
            placeholder={isMonthOnly ? 'Select month' : 'Select date'}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            monthOnly={isMonthOnly}
            showMonthDropdown={true}
            showYearDropdown={true}
          />
          {isTouched && !selectedDate && (
            <div className='field-error'>
              {isMonthOnly ? 'Month is required' : 'Date is required'}
            </div>
          )}
        </div>

        <div className='form-row'>
          <Select
            name='pmp_plan_reference'
            title='Plan Reference Number *'
            placeholder={
              isLoadingOptions
                ? 'Loading PMP references...'
                : planOptions.length
                  ? 'Select PMP reference number'
                  : 'No PMP references available'
            }
            options={planOptions}
            selectedOption={selectedPlan}
            onChange={(option) => setSelectedPlan(option)}
            isError={isTouched && !selectedPlan}
            preventAutoOpenOnLoad={true}
            useMenuPortal={true}
            isDisable={isLoadingOptions || planOptions.length === 0}
          />
          {isTouched && !selectedPlan && (
            <div className='field-error'>Plan reference number is required</div>
          )}
        </div>

        <div className='form-row'>
          <TextInput
            name='finding_title'
            title='Finding Title *'
            placeholder='Enter finding title'
            value={findingTitle}
            onChange={(event) => setFindingTitle(event.target.value)}
            isError={isTouched && !findingTitle.trim()}
          />
          {isTouched && !findingTitle.trim() && (
            <div className='field-error'>Finding title is required</div>
          )}
        </div>

        <div className='form-row'>
          <TextArea
            name='finding_context'
            title='Finding Context *'
            placeholder='Enter finding context'
            value={findingContext}
            onChange={(event) => setFindingContext(event.target.value)}
            rows={3}
            isError={isTouched && !findingContext.trim()}
          />
          {isTouched && !findingContext.trim() && (
            <div className='field-error'>Finding context is required</div>
          )}
        </div>

        <div className='form-row'>
          <TextArea
            name='clinical_context'
            title='Clinical Context (optional)'
            placeholder='Enter clinical context'
            value={clinicalContext}
            onChange={(event) => setClinicalContext(event.target.value)}
            rows={3}
          />
        </div>

        <div className='form-row'>
          <Select
            name='modality'
            title='Modality *'
            placeholder={
              isLoadingOptions
                ? 'Loading modalities...'
                : modalityOptions.length
                  ? 'Select modality'
                  : 'No active modalities'
            }
            options={modalityOptions}
            selectedOption={selectedModality}
            onChange={(option) => {
              pricesEditedRef.current = false;
              setSelectedModality(option);
            }}
            isError={isTouched && !selectedModality}
            preventAutoOpenOnLoad={true}
            useMenuPortal={true}
            isDisable={isLoadingOptions || modalityOptions.length === 0}
          />
          {isTouched && !selectedModality && (
            <div className='field-error'>Modality is required</div>
          )}
        </div>

        <div className='form-row'>
          <TextInput
            name='modality_display'
            title='Modality display *'
            placeholder='e.g. ultrasound of the liver'
            value={modalityDisplay}
            onChange={(event) => {
              displayEditedRef.current = true;
              setModalityDisplay(event.target.value);
            }}
            isError={isTouched && Boolean(modalityDisplayError)}
          />
          {isTouched && modalityDisplayError && (
            <div className='field-error'>{modalityDisplayError}</div>
          )}
        </div>

        <div className='form-row'>
          <TextArea
            name='modality_prep'
            title='Modality prep (optional)'
            placeholder='Enter modality prep'
            value={modalityPrep}
            onChange={(event) => setModalityPrep(event.target.value)}
            rows={3}
          />
        </div>

        <div className='form-row-pair'>
          <div className='form-row'>
            <TextInput
              name='full_pricing'
              title='Full price'
              placeholder='Standard price'
              type='number'
              value={fullPrice}
              onChange={(event) => {
                if (event.target.value.includes('-')) return;
                pricesEditedRef.current = true;
                setFullPrice(event.target.value);
              }}
              isError={isTouched && Boolean(fullPriceError)}
            />
            {isTouched && fullPriceError && (
              <div className='field-error'>{fullPriceError}</div>
            )}
          </div>
          <div className='form-row'>
            <TextInput
              name='member_pricing'
              title='Member price'
              placeholder="This person's price"
              type='number'
              value={memberPrice}
              onChange={(event) => {
                if (event.target.value.includes('-')) return;
                pricesEditedRef.current = true;
                setMemberPrice(event.target.value);
              }}
              isError={isTouched && Boolean(memberPriceError)}
            />
            {isTouched && memberPriceError && (
              <div className='field-error'>{memberPriceError}</div>
            )}
          </div>
        </div>

        <div className='form-actions'>
          <Button
            type='submit'
            text={isEdit ? 'Update' : 'Submit'}
            isLoading={isSubmitting}
            isDisabled={isSubmitDisabled}
            width='100%'
          />
        </div>
      </form>
    </Modal>
  );
};

export default CreatePmpAlertModal;
